import React from 'react';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { cn } from 'src/lib/utils';
import { Plus, Minus } from 'lucide-react';

import { Label } from 'src/components/ui/label';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

export const DynamicFieldArray = ({
  control,
  name,
  options = [],
  label,
  isOptional = false,
  error,
  showClearAll = false,
  disabled = false,
}) => {
  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
  });

  const values = useWatch({
    control,
    name,
    defaultValue: [],
  });

  const getSelectedValues = () =>
    Array.isArray(values)
      ? values.map((item) => item?.value).filter(Boolean)
      : [];

  const isLastFieldEmpty = (currentValues) =>
    Array.isArray(currentValues) && currentValues.length > 0
      ? !currentValues[currentValues.length - 1]?.value
      : false;

  const filteredOptions = (selectedValues, currentValue) =>
    Array.isArray(options)
      ? options.filter(
          (option) =>
            option?.value &&
            (!selectedValues.includes(option.value) ||
              option.value === currentValue)
        )
      : [];

  React.useEffect(() => {
    if (fields.length === 0) {
      append({ value: '', isMandatory: true });
    }
  }, [append, fields.length]);

  return (
    <div className="space-y-4 relative">
      <div className="flex items-center mb-1">
        <Label htmlFor={name}>{label}</Label>
        {!isOptional && <span className="text-red-500 ml-1">*</span>}
      </div>
      {error && <p className="text-sm text-red-500 mb-2">{error.message}</p>}

      <div className="space-y-3">
        {fields.map((field, index) => {
          const selectedValues = getSelectedValues();
          const availableOptions = filteredOptions(
            selectedValues,
            values?.[index]?.value
          );

          return (
            <div key={field.id} className="flex items-center gap-3">
              <div className="flex-1">
                <Controller
                  name={`${name}.${index}.value`}
                  control={control}
                  rules={{
                    required: !isOptional && values?.[index]?.isMandatory,
                  }}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error: fieldError },
                  }) => (
                    <Select
                      onValueChange={(newValue) => {
                        onChange(newValue);
                        update(index, {
                          ...values?.[index],
                          value: newValue,
                        });
                      }}
                      value={value || ''}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        className={cn(
                          'w-full h-10 bg-brand-bg-subtle border-input focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]',
                          fieldError ? 'border-destructive' : 'border-input'
                        )}
                        disabled={disabled}
                      >
                        <SelectValue placeholder={`Select ${label}`}>
                          {value
                            ? options.find((opt) => opt.value === value)?.label
                            : ''}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableOptions.map((option) => (
                          <SelectItem
                            key={`${name}-${option.value}`}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="min-w-40 flex-shrink-0">
                <Controller
                  name={`${name}.${index}.isMandatory`}
                  control={control}
                  defaultValue
                  render={({ field: mandatoryField }) => {
                    const isMandatory = mandatoryField.value !== false;
                    return (
                      <div className="relative flex border border-input rounded-full p-1 bg-card text-xs">
                        <div
                          className={cn(
                            'absolute top-1 bottom-1 rounded-full bg-primary shadow-sm transition-all duration-300 ease-in-out',
                            isMandatory
                              ? 'left-1 w-[calc(50%-2px)]'
                              : 'left-[calc(50%+1px)] w-[calc(50%-4px)]'
                          )}
                        />

                        <button
                          type="button"
                          onClick={() => mandatoryField.onChange(true)}
                          className={cn(
                            'relative flex-1 py-2 px-3 text-xs font-medium rounded-full transition-all duration-300 z-9',
                            isMandatory ? 'text-white' : 'text-gray-600'
                          )}
                        >
                          Mandatory
                        </button>

                        <button
                          type="button"
                          onClick={() => mandatoryField.onChange(false)}
                          className={cn(
                            'relative flex-1 py-2 px-3 text-xs font-medium rounded-full transition-all duration-300 z-9',
                            !isMandatory ? 'text-white' : 'text-gray-600'
                          )}
                        >
                          Optional
                        </button>
                      </div>
                    );
                  }}
                />
              </div>

              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(index)}
                  className="h-10 w-10 p-0 text-gray-500 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        size="sm"
        variant="addField"
        onClick={() => append({ value: '', isMandatory: true })}
        disabled={isLastFieldEmpty(values)}
        className="min-w-fit max-w-xs border-dashed border-gray-300 text-gray-600 hover:bg-muted whitespace-nowrap py-4"
      >
        <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
        <span className="hidden sm:inline">
          Add {label.toLowerCase().slice(0, -1)}
        </span>
        <span className="sm:hidden">
          Add {label.toLowerCase().slice(0, -1)}
        </span>
      </Button>

      <div className="flex items-center justify-end absolute right-0 top-[-60%]">
        {showClearAll && fields.length > 0 && (
          <button
            type="button"
            onClick={() => {
              remove();
              append({ value: '', isMandatory: true });
            }}
            className="text-gray-700 hover:text-foreground-strong text-xs font-medium p-2 hover:bg-muted rounded-lg hover:text-red-500"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
